/**
 * Calls tab of the account page: log or record a call, schedule a meeting, add a comment, and
 * see what's coming up and what was said. Everything is a Follow-up task on the account in
 * zInspector (providers/zinspector/activities.ts), so it works on the live API.
 */
import { useGetList } from "ra-core";
import { useMemo } from "react";

import type { ZcrmAccount } from "../providers/zinspector/accounts";
import type { Contact } from "../types";
import { ActivityList } from "./ActivityList";
import { LogActivityForm } from "./LogActivityForm";

const ALL = { page: 1, perPage: 500 };

export const CallsTab = ({ account }: { account: ZcrmAccount }) => {
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    filter: { company_id: account.id },
    pagination: ALL,
    sort: { field: "last_name", order: "ASC" },
  });
  const people = useMemo(
    () =>
      contacts.map((c) => ({
        id: c.id,
        name: `${c.first_name} ${c.last_name}`.trim(),
      })),
    [contacts],
  );
  const contactNames = useMemo(
    () => new Map(people.map((p) => [String(p.id), p.name])),
    [people],
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">Calls with {account.name}</h2>
        <p className="text-sm text-muted-foreground">
          Log or record a call, schedule a meeting, or leave a comment for the
          team. Each one is saved on the account in zInspector.
        </p>
      </div>
      <LogActivityForm
        companyId={account.id}
        accountName={account.name}
        contacts={people}
      />
      <ActivityList companyId={account.id} contactNames={contactNames} />
    </div>
  );
};
